import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { ChipsInput } from '@shared/ui/ChipsInput';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import {
  KADRY_SCHEDULE_KINDS,
  KADRY_SCHEDULE_KIND_LABELS,
  KADRY_SOURCES,
  KADRY_SOURCE_HINTS,
  KADRY_SOURCE_LABELS,
  KADRY_NON_DISCRIMINATION_HINT,
  KADRY_YEARS_IN_ROLE_MAX,
  kadryErrorCode,
  kadryErrorDetails,
  kadryProfileIssues,
  type KadryScheduleKind,
  type KadrySearchProfile,
  type KadrySourceId,
  type KadryWorkSite,
} from '@entities/kadry';
import {
  KadryCheckRow,
  KadryCheckbox,
  KadryFieldError,
  KadryFieldGrid,
  KadryFieldHint,
  KadryFormActions,
  KadryFormSection,
  kadryParseNumber,
} from '../ui';

/* Форма профиля поиска по Прил. Е. Полей про возраст, пол и место проживания
   нет намеренно (ТЗ §1.6): от кандидата учитывается только готовность работать
   в месте и режиме заявки. */

interface FormState {
  position: string;
  synonyms: string[];
  skillsRequired: string[];
  skillsOptional: string[];
  permits: string[];
  years: string;
  workSiteId: string;
  scheduleType: KadryScheduleKind | '';
  shift: string;
  rotationDays: string;
  rotation: boolean;
  relocation: boolean;
  businessTrips: boolean;
  payFrom: string;
  payTo: string;
  sources: KadrySourceId[];
}

const numStr = (n: number | null | undefined) => (n == null ? '' : String(n));

function toForm(p: KadrySearchProfile): FormState {
  return {
    position: p.position ?? '',
    synonyms: p.synonyms ?? [],
    skillsRequired: p.skills?.required ?? [],
    skillsOptional: p.skills?.optional ?? [],
    permits: p.permits?.required ?? [],
    years: numStr(p.experience?.years_in_role_min),
    workSiteId: p.work_site_id ?? '',
    scheduleType: p.schedule?.type ?? '',
    shift: p.schedule?.shift ?? '',
    rotationDays: numStr(p.schedule?.rotation_days),
    rotation: Boolean(p.readiness_required?.rotation),
    relocation: Boolean(p.readiness_required?.relocation),
    businessTrips: Boolean(p.readiness_required?.business_trips),
    payFrom: numStr(p.pay?.from),
    payTo: numStr(p.pay?.to),
    sources: p.sources ?? [],
  };
}

function fromForm(f: FormState): KadrySearchProfile {
  const years = kadryParseNumber(f.years);
  const rotationDays = kadryParseNumber(f.rotationDays);
  const payFrom = kadryParseNumber(f.payFrom);
  const payTo = kadryParseNumber(f.payTo);
  return {
    position: f.position.trim(),
    synonyms: f.synonyms,
    skills: { required: f.skillsRequired, optional: f.skillsOptional },
    permits: { required: f.permits },
    experience: { years_in_role_min: years },
    work_site_id: f.workSiteId || null,
    schedule: f.scheduleType
      ? {
          type: f.scheduleType,
          shift: f.shift.trim() || null,
          rotation_days: f.scheduleType === 'rotation' ? rotationDays : null,
        }
      : null,
    readiness_required: {
      rotation: f.rotation,
      relocation: f.relocation,
      business_trips: f.businessTrips,
    },
    pay: payFrom == null && payTo == null ? null : { from: payFrom, to: payTo },
    // Порядок источников — как в справочнике (сервер сортирует так же).
    sources: KADRY_SOURCES.filter((s) => f.sources.includes(s)),
  };
}

/** Ошибки формата чисел: с ними сервер не примет даже черновик. */
function formatErrors(f: FormState): Partial<Record<keyof FormState, string>> {
  const e: Partial<Record<keyof FormState, string>> = {};
  const years = kadryParseNumber(f.years);
  if (years != null && (Number.isNaN(years) || years < 0)) e.years = 'Число лет от 0';
  const rd = kadryParseNumber(f.rotationDays);
  if (f.scheduleType === 'rotation' && rd != null && (!Number.isInteger(rd) || rd < 0 || rd > 65535)) {
    e.rotationDays = 'Целое число дней';
  }
  for (const k of ['payFrom', 'payTo'] as const) {
    const v = kadryParseNumber(f[k]);
    if (v != null && (!Number.isInteger(v) || v < 0)) e[k] = 'Целое число рублей от 0';
  }
  return e;
}

/** Поле профиля в нотации сервера (`details.field`) → поле формы. */
const FIELD_TO_FORM: Record<string, keyof FormState> = {
  position: 'position',
  synonyms: 'synonyms',
  'skills.required': 'skillsRequired',
  'skills.optional': 'skillsOptional',
  'permits.required': 'permits',
  'experience.years_in_role_min': 'years',
  work_site_id: 'workSiteId',
  'schedule.shift': 'shift',
  'schedule.rotation_days': 'rotationDays',
  pay: 'payFrom',
  'pay.from': 'payFrom',
  'pay.to': 'payTo',
  sources: 'sources',
};

/** Относится ли серверная ошибка проверки к полю формы профиля. */
export function kadryIsProfileFieldError(err: unknown): boolean {
  return profileErrorField(err) !== undefined;
}

function profileErrorField(err: unknown): keyof FormState | undefined {
  if (kadryErrorCode(err) !== 'validation_error') return undefined;
  return FIELD_TO_FORM[String(kadryErrorDetails(err).field ?? '')];
}

/** Каноническое представление профиля для сравнения версий. */
const canon = (p: KadrySearchProfile) => JSON.stringify(fromForm(toForm(p)));

const Summary = styled.ul`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(7)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Gap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const InlineActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const SourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

interface Props {
  initial: KadrySearchProfile;
  /** Ревизия черновика, из которой построена форма (для If-Match). */
  revision?: number;
  workSites: KadryWorkSite[];
  busy: boolean;
  submitLabel: string;
  /** Разрешить отправку без изменений (новый черновик создаётся из исходной формы). */
  allowPristineSubmit?: boolean;
  /**
   * Сохранение; форма остаётся открытой, пока родитель её не закроет.
   * `baseRevision` — ревизия, с которой начата правка: If-Match уходит с ней,
   * и чужое сохранение за это время не затирается (сервер ответит конфликтом).
   */
  onSubmit: (profile: KadrySearchProfile, baseRevision: number | undefined) => void;
  onCancel: () => void;
  /** Есть несохранённые изменения (для блокировки утверждения). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Ошибка сохранения (`validation_error` показывается под своим полем). */
  serverError?: unknown;
  /** Баннер ошибки и дополнительные кнопки (утверждение) — от родителя. */
  errorSlot?: ReactNode;
  extraActions?: ReactNode;
  /**
   * «Сохранить и утвердить»: сохранить форму и сразу перейти к утверждению.
   * Кнопка доступна, когда профиль заполнен полностью.
   */
  onSubmitAndApprove?: (profile: KadrySearchProfile, baseRevision: number | undefined) => void;
}

/** Форма черновика профиля поиска (Прил. Е `SearchProfile`). */
export function ProfileEditor({
  initial,
  revision,
  workSites,
  busy,
  submitLabel,
  allowPristineSubmit = false,
  onSubmit,
  onCancel,
  onDirtyChange,
  serverError,
  errorSlot,
  extraActions,
  onSubmitAndApprove,
}: Props) {
  const [f, setF] = useState<FormState>(() => toForm(initial));
  // База правки: с какой версией профиля и ревизией начали редактировать.
  const [base, setBase] = useState(() => ({ json: canon(initial), revision }));
  // Новая версия с сервера, пришедшая во время несохранённой правки.
  const [pending, setPending] = useState<{ profile: KadrySearchProfile; revision?: number } | null>(
    null,
  );
  const lastSubmitted = useRef<string | null>(null);

  const profile = fromForm(f);
  const current = JSON.stringify(profile);
  const dirty = current !== base.json;

  // С сервера пришла другая версия черновика. Без несохранённых правок (или
  // если это результат нашего же сохранения) — принимаем молча; при правках
  // форму не трогаем и спрашиваем, загрузить ли серверную версию.
  useEffect(() => {
    const incoming = canon(initial);
    if (incoming === base.json && revision === base.revision) return;
    if (!dirty || current === incoming || lastSubmitted.current === current) {
      setF(toForm(initial));
      setBase({ json: incoming, revision });
      setPending(null);
      lastSubmitted.current = null;
    } else {
      setPending({ profile: initial, revision });
    }
    // Реагируем только на новую версию с сервера, не на ввод.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, revision]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  function loadPending() {
    if (!pending) return;
    setF(toForm(pending.profile));
    setBase({ json: canon(pending.profile), revision: pending.revision });
    setPending(null);
  }

  const fmt = formatErrors(f);
  const hasFormatErrors = Object.keys(fmt).length > 0;
  const issues = kadryProfileIssues(profile);
  const serverField = profileErrorField(serverError);
  const serverReason = serverField ? String(kadryErrorDetails(serverError).reason ?? '') : '';

  /** Замечания к полю: формат ввода, проверки профиля (§1.6 и др.), ответ сервера. */
  function fieldErrors(k: keyof FormState): string[] {
    const out: string[] = [];
    const own = fmt[k];
    if (own) out.push(own);
    for (const i of issues) if (FIELD_TO_FORM[i.field] === k) out.push(i.message);
    if (serverField === k && serverReason) out.push(`Сервер: ${serverReason}`);
    return out;
  }
  const errorsOf = (k: keyof FormState) =>
    fieldErrors(k).map((m) => <KadryFieldError key={m}>{m}</KadryFieldError>);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((p) => ({ ...p, [k]: v }));
  const toggleSource = (s: KadrySourceId, on: boolean) =>
    set('sources', on ? [...f.sources, s] : f.sources.filter((x) => x !== s));

  const siteOptions = workSites.filter((s) => s.active || s.id === f.workSiteId);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || hasFormatErrors) return;
    lastSubmitted.current = current;
    onSubmit(profile, base.revision);
  }

  return (
    <form onSubmit={submit} noValidate>
      {pending && (
        <Gap>
          <FallbackBanner tone="warning" title="Версия на сервере изменилась">
            Пока вы редактировали, черновик сохранили в другом окне или другой пользователь.
            Загрузить версию с сервера? Ваши несохранённые правки пропадут. Если оставить свои —
            при сохранении сервер сообщит о конфликте.
            <InlineActions>
              <Button type="button" $size="sm" onClick={loadPending}>
                Загрузить с сервера
              </Button>
              <Button type="button" $size="sm" $variant="ghost" onClick={() => setPending(null)}>
                Оставить мои правки
              </Button>
            </InlineActions>
          </FallbackBanner>
        </Gap>
      )}
      <KadryFormSection>
        <legend>Кого ищем</legend>
        <Gap>
          <KadryFieldHint>{KADRY_NON_DISCRIMINATION_HINT}</KadryFieldHint>
        </Gap>
        <Field>
          <Label htmlFor="kp-position">Должность *</Label>
          <Input
            id="kp-position"
            value={f.position}
            maxLength={200}
            placeholder="Например: Электросварщик ручной сварки"
            onChange={(e) => set('position', e.target.value)}
          />
          {errorsOf('position')}
        </Field>
        <Field>
          <Label htmlFor="kp-synonyms">Синонимы должности</Label>
          <ChipsInput
            id="kp-synonyms"
            value={f.synonyms}
            onChange={(v) => set('synonyms', v)}
            maxItems={30}
            maxLength={120}
            placeholder="Введите и нажмите Enter: сварщик, электросварщик…"
          />
          <KadryFieldHint>Как ещё называют эту должность в резюме — до 30 вариантов.</KadryFieldHint>
          {errorsOf('synonyms')}
        </Field>
        <KadryFieldGrid>
          <Field>
            <Label htmlFor="kp-skills-req">Навыки обязательные</Label>
            <ChipsInput
              id="kp-skills-req"
              value={f.skillsRequired}
              onChange={(v) => set('skillsRequired', v)}
              maxItems={50}
              maxLength={120}
              placeholder="Enter — добавить"
              dragGroup="kp-skills"
            />
            <KadryFieldHint>
              Навыки перетаскиваются мышью между обязательными и желательными.
            </KadryFieldHint>
            {errorsOf('skillsRequired')}
          </Field>
          <Field>
            <Label htmlFor="kp-skills-opt">Навыки желательные</Label>
            <ChipsInput
              id="kp-skills-opt"
              value={f.skillsOptional}
              onChange={(v) => set('skillsOptional', v)}
              maxItems={50}
              maxLength={120}
              placeholder="Enter — добавить"
              dragGroup="kp-skills"
            />
            <KadryFieldHint>Совпадающие с обязательными уберутся при сохранении.</KadryFieldHint>
            {errorsOf('skillsOptional')}
          </Field>
        </KadryFieldGrid>
        <Field>
          <Label htmlFor="kp-permits">Допуски обязательные</Label>
          <ChipsInput
            id="kp-permits"
            value={f.permits}
            onChange={(v) => set('permits', v)}
            maxItems={30}
            maxLength={160}
            placeholder="НАКС, работы на высоте, электробезопасность…"
          />
          {errorsOf('permits')}
        </Field>
        <KadryFieldGrid>
          <Field>
            <Label htmlFor="kp-years">Минимум лет в профильной роли</Label>
            <Input
              id="kp-years"
              type="number"
              inputMode="decimal"
              min={0}
              max={KADRY_YEARS_IN_ROLE_MAX}
              step={0.5}
              value={f.years}
              placeholder="Не важно"
              onChange={(e) => set('years', e.target.value)}
            />
            {fieldErrors('years').length > 0 ? (
              errorsOf('years')
            ) : (
              <KadryFieldHint>
                Опыт именно в этой роли, от 0 до {KADRY_YEARS_IN_ROLE_MAX} лет: общий стаж и опыт
                сверх {KADRY_YEARS_IN_ROLE_MAX} лет не учитываются (§1.6).
              </KadryFieldHint>
            )}
          </Field>
          <Field>
            <Label htmlFor="kp-site">Объект</Label>
            <Select id="kp-site" value={f.workSiteId} onChange={(e) => set('workSiteId', e.target.value)}>
              <option value="">Не указан</option>
              {siteOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.active ? '' : ' (выведен)'}
                </option>
              ))}
            </Select>
            <KadryFieldHint>Место работы задаётся заявкой, а не местом жительства кандидата.</KadryFieldHint>
            {errorsOf('workSiteId')}
          </Field>
        </KadryFieldGrid>
      </KadryFormSection>

      <KadryFormSection>
        <legend>График и готовность</legend>
        <KadryFieldGrid $cols={3} $alignEnd>
          <Field>
            <Label htmlFor="kp-schedule">График</Label>
            <Select
              id="kp-schedule"
              value={f.scheduleType}
              onChange={(e) => set('scheduleType', e.target.value as KadryScheduleKind | '')}
            >
              <option value="">Не задан</option>
              {KADRY_SCHEDULE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KADRY_SCHEDULE_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
          {f.scheduleType && (
            <Field>
              <Label htmlFor="kp-shift">Смена</Label>
              <Input
                id="kp-shift"
                value={f.shift}
                maxLength={60}
                placeholder="2/2, день/ночь, 5/2"
                onChange={(e) => set('shift', e.target.value)}
              />
              {errorsOf('shift')}
            </Field>
          )}
          {f.scheduleType === 'rotation' && (
            <Field>
              <Label htmlFor="kp-rotation">Длительность вахты, дней</Label>
              <Input
                id="kp-rotation"
                type="number"
                inputMode="numeric"
                min={1}
                max={120}
                step={1}
                value={f.rotationDays}
                placeholder="30"
                onChange={(e) => set('rotationDays', e.target.value)}
              />
              {errorsOf('rotationDays')}
            </Field>
          )}
        </KadryFieldGrid>
        <Field>
          <Label as="div">Кандидату нужна готовность</Label>
          <KadryCheckRow>
            <KadryCheckbox checked={f.rotation} onChange={(v) => set('rotation', v)}>
              к вахте
            </KadryCheckbox>
            <KadryCheckbox checked={f.relocation} onChange={(v) => set('relocation', v)}>
              к переезду на объект
            </KadryCheckbox>
            <KadryCheckbox checked={f.businessTrips} onChange={(v) => set('businessTrips', v)}>
              к командировкам
            </KadryCheckbox>
          </KadryCheckRow>
          <KadryFieldHint>
            Неизвестная готовность кандидата — повод уточнить, а не отказать.
          </KadryFieldHint>
        </Field>
      </KadryFormSection>

      <KadryFormSection>
        <legend>Оплата и источники</legend>
        <KadryFieldGrid>
          <Field>
            <Label htmlFor="kp-pay-from">Оплата от, ₽/мес на руки</Label>
            <Input
              id="kp-pay-from"
              type="number"
              inputMode="numeric"
              min={0}
              step={1000}
              value={f.payFrom}
              onChange={(e) => set('payFrom', e.target.value)}
            />
            {errorsOf('payFrom')}
          </Field>
          <Field>
            <Label htmlFor="kp-pay-to">Оплата до, ₽/мес на руки</Label>
            <Input
              id="kp-pay-to"
              type="number"
              inputMode="numeric"
              min={0}
              step={1000}
              value={f.payTo}
              onChange={(e) => set('payTo', e.target.value)}
            />
            {errorsOf('payTo')}
          </Field>
        </KadryFieldGrid>
        <Field>
          <Label as="div">Где искать *</Label>
          <SourceGrid>
            {KADRY_SOURCES.map((s) => (
              <KadryCheckbox
                key={s}
                checked={f.sources.includes(s)}
                onChange={(on) => toggleSource(s, on)}
                hint={KADRY_SOURCE_HINTS[s]}
              >
                {KADRY_SOURCE_LABELS[s]}
              </KadryCheckbox>
            ))}
          </SourceGrid>
          <KadryFieldHint>
            Площадка участвует в поиске, только когда её включит администратор подбора.
          </KadryFieldHint>
          {errorsOf('sources')}
        </Field>
      </KadryFormSection>

      {issues.length > 0 && (
        <Summary aria-label="Что нужно исправить перед утверждением">
          {issues.map((i) => (
            <li key={i.field + i.message}>{i.message}</li>
          ))}
        </Summary>
      )}
      {errorSlot}
      <KadryFormActions>
        <Button type="button" $variant="secondary" onClick={onCancel} disabled={busy}>
          {dirty ? 'Отменить изменения' : 'Закрыть'}
        </Button>
        <Button
          type="submit"
          $variant={onSubmitAndApprove ? 'secondary' : 'primary'}
          disabled={busy || hasFormatErrors || (!dirty && !allowPristineSubmit)}
        >
          {busy ? 'Сохраняем…' : submitLabel}
        </Button>
        {onSubmitAndApprove && (
          <Button
            type="button"
            disabled={busy || hasFormatErrors || issues.length > 0}
            title={
              issues.length > 0 ? 'Профиль заполнен не полностью — см. замечания выше' : undefined
            }
            onClick={() => {
              if (busy || hasFormatErrors) return;
              lastSubmitted.current = current;
              onSubmitAndApprove(profile, base.revision);
            }}
          >
            Сохранить и утвердить
          </Button>
        )}
        {extraActions}
      </KadryFormActions>
    </form>
  );
}
